import { useState, useCallback } from 'react';
import GameResourceList from './GameResourceList';
import _ from 'lodash';
import confetti from 'canvas-confetti';
import { FaCheck } from 'react-icons/fa6';
import { initialPicks, initialProb, initialResults } from '../App';
import { calculateGhostDemands } from '../calculateGhostDemands';

const getContextValues = (initialObject, oldObject, key, value) => {
    const clonedObject = { ...oldObject };
    const keys = Object.keys(clonedObject);
    for (let objectKey of keys) {
        if (Number(objectKey) === key) {
            clonedObject[objectKey] = value;
        } else if (objectKey > key) {
            clonedObject[objectKey] = initialObject[objectKey];
        }
    }
    return clonedObject;
};

function Game({ resources, choice, setChoice, brain, setBrain, picks, setPicks, results, setResults, prob, setProb, jokerData, setJokerData }) {
    const [error, setError] = useState(null);

    const showError = useCallback((msg) => {
        setError(msg);
        setTimeout(() => setError(null), 4000);
    }, []);

    const getQuery = (choice) => {
        let query = '';
        if (choice >= 2) query += picks[1].join('') + '/' + results[1].join('');
        if (choice >= 3) query += '/' + picks[2].join('') + '/' + results[2].join('');
        if (choice >= 4) query += '/' + picks[3].join('') + '/' + results[3].join('');
        if (choice >= 5) query += '/' + picks[4].join('') + '/' + results[4].join('');
        return query;
    }

    const fireConfetti = () => {
        const end = Date.now() + 2000;
        const frame = () => {
            confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.6 } });
            confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.6 } });
            if (Date.now() < end) requestAnimationFrame(frame);
        };
        frame();
    };

    const handleChoiceSubmit = async (choice) => {
        // Check for win: all 5 results are Green
        const currentResults = results[choice];
        if (currentResults && currentResults.every(r => r === 'G')) {
            fireConfetti();
            return;
        }

        const resourceCount = resources.length;
        const nextChoice = choice + 1;

        if (resourceCount === 3 && nextChoice > 3) return;
        if (resourceCount === 4 && nextChoice > 4) return;
        if (resourceCount > 5 && nextChoice > 5) return;

        const data = _.get(brain, `${resourceCount}.${nextChoice}`);
        const query = getQuery(nextChoice);

        if (!data) {
            fetch(`${process.env.PUBLIC_URL}/json/R${resourceCount}-C${nextChoice}.json`)
                .then((response) => {
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                    return response.json();
                })
                .then((tempData) => {
                    setBrain((prev) => ({ ...prev, [`${resourceCount}.${nextChoice}`]: tempData }));
                    const nextCombo = _.get(tempData, query);
                    if (!nextCombo) { showError('Combination not found — double-check your colors and try again.'); return; }
                    const splitCombo = nextCombo.next_combo.split('');
                    const nextPicks = splitCombo.map((num) => num === '-' ? '-' : parseInt(num));
                    setPicks(getContextValues(initialPicks, picks, nextChoice, nextPicks));
                    setResults(getContextValues(initialResults, results, nextChoice, nextPicks.map((pick) => pick === '-' ? 'G' : 'Y')));
                    setProb(getContextValues(initialProb, prob, nextChoice, nextCombo.prob));
                    setChoice(nextChoice);
                })
                .catch((error) => console.error("Failed to load JSON:", error));
        } else {
            const nextCombo = _.get(data, query);
            if (!nextCombo) { showError('Combination not found — double-check your colors and try again.'); return; }
            const splitCombo = nextCombo.next_combo.split('');
            const nextPicks = splitCombo.map((num) => num === '-' ? '-' : parseInt(num));
            setPicks(getContextValues(initialPicks, picks, nextChoice, nextPicks));
            setResults(getContextValues(initialResults, results, nextChoice, nextPicks.map((pick) => pick === '-' ? 'G' : 'Y')));
            setProb(getContextValues(initialProb, prob, nextChoice, nextCombo.prob));
            setChoice(nextChoice);
        }

        const jokerResult = calculateGhostDemands(resources, picks, results);
        setJokerData(jokerResult);
    }

    const isLastRound = (num) =>
        (num === 3 && resources.length === 3) ||
        (num === 4 && resources.length === 4) ||
        (num === 5 && resources.length >= 5);

    const choiceRow = (num, showProb) => {
        const isCurrent = choice === num;
        const isActive = choice >= num;
        const isPast = choice > num;
        const isVisible = num <= 3 || choice >= num;
        if (!isVisible) return null;

        return (
            <div
                key={num}
                className={`
                    flex items-end gap-1 md:gap-2 px-1.5 md:px-4 py-1.5 md:py-2 rounded-xl transition-all duration-300
                    ${isCurrent ? 'bg-white/[0.04] border border-white/[0.08]' : 'border border-transparent'}
                    ${isPast ? 'opacity-60' : ''}
                `}>
                <div className="flex flex-col items-center shrink-0">
                    { isCurrent && <span className="h-4 md:h-5 text-white/40 text-[11px] md:text-sm font-medium tabular-nums leading-4 md:leading-5">
                        {showProb && prob[num] ? prob[num] : '\u00A0'}
                    </span> }
                    <div className={`
                        flex items-center justify-center w-8 h-10 md:w-14 md:h-14 rounded-lg text-sm md:text-base font-bold shrink-0 transition-all border
                        ${isCurrent ? 'bg-primary/15 text-primary shadow-glow-primary border-primary/30' : 'text-white/30 border-transparent'}
                    `}>
                        {num}
                    </div>
                </div>

                <GameResourceList
                    resources={resources}
                    picks={picks[num]}
                    results={results[num]}
                    setResults={setResults}
                    choice={num}
                    setChoice={setChoice}
                    interactive={isCurrent}
                    possibilityCounts={isCurrent ? jokerData?.possibilities : null}
                    allResources={isCurrent ? resources : null} />

                { isActive && !isLastRound(num) && num <= choice ? (
                    <div className="flex flex-col items-center shrink-0">
                        { isCurrent && <span className="h-4 md:h-5" /> }
                        <button
                            className="flex items-center justify-center w-10 h-10 md:w-14 md:h-14 rounded-lg bg-accent/80 hover:bg-accent text-white text-sm md:text-base transition-all hover:shadow-glow-accent border border-accent/50"
                            onClick={() => handleChoiceSubmit(num)}>
                            <FaCheck />
                        </button>
                    </div>
                ) : <div className="w-10 md:w-14 shrink-0" /> }
            </div>
        );
    };

    return (
        <div className="flex flex-col items-center gap-1">
            {choiceRow(1, false)}
            {choiceRow(2, true)}
            {choiceRow(3, true)}
            {choice >= 4 && choiceRow(4, true)}
            {choice >= 5 && choiceRow(5, true)}
            {jokerData?.jokerRecommended && resources.length >= 6 && (
                <div className="w-full">
                    {/* Arrow row mirrors the choice-row layout (padding, number column,
                        gaps, slot widths) so the arrow lines up at every breakpoint. */}
                    <div className="flex gap-1 md:gap-2 px-1.5 md:px-4">
                        <div className="w-8 md:w-14 shrink-0" />
                        <div className="flex gap-1 md:gap-2">
                            {[0, 1, 2, 3, 4].map((slot) => (
                                <div key={slot} className="w-10 md:w-14 flex justify-center">
                                    {slot === jokerData.jokerGhostIndex && (
                                        <div
                                            className="w-0 h-0"
                                            style={{
                                                borderLeft: '10px solid transparent',
                                                borderRight: '10px solid transparent',
                                                borderBottom: '10px solid rgba(122, 162, 196, 0.3)',
                                            }} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-center gap-2.5 w-full px-4 py-2 rounded-xl bg-primary/15 border border-primary/30 text-primary-light text-sm font-semibold animate-joker-pulse">
                        <img src={`${process.env.PUBLIC_URL}/images/diplomacyJoker.png`} alt="Joker" className="w-7 h-7" />
                        <span>You could use a joker on Ghost {jokerData.jokerGhostIndex + 1}</span>
                    </div>
                </div>
            )}
            {error && (
                <div className="w-full px-4 py-2.5 rounded-xl bg-status-red/15 border border-status-red/30 text-status-red text-xs font-medium text-center animate-[fadeIn_0.2s_ease-out]">
                    {error}
                </div>
            )}
        </div>
    );
}

export default Game;
